import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { AREAS, FIRM } from "@/lib/content";
import { Mail, MapPin, Phone, Clock, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputBase =
    "bg-[#0A0A0B] border-white/10 text-[#F4F4F5] placeholder:text-[#A1A1AA]/60 focus-visible:ring-[#C4A977]/40 focus-visible:border-[#C4A977] rounded-sm h-12";

export default function ContactForm({ variant = "full" }) {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
        area: "",
    });
    const [loading, setLoading] = useState(false);

    const handle = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        // Read directly from the DOM in case Chromebook/autofill bypassed React state.
        const fd = new FormData(e.currentTarget);
        const payload = {
            name: (fd.get("name") || form.name || "").toString().trim(),
            email: (fd.get("email") || form.email || "").toString().trim(),
            phone: (fd.get("phone") || form.phone || "").toString().trim(),
            subject: (fd.get("subject") || form.subject || "").toString().trim(),
            message: (fd.get("message") || form.message || "").toString().trim(),
            area: form.area || "",
        };
        if (
            !payload.name ||
            !payload.email ||
            !payload.subject ||
            !payload.message
        ) {
            toast.error("Vänligen fyll i alla obligatoriska fält.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/contact`, payload);
            toast.success(data.message || "Tack! Vi återkommer inom kort.");
            setForm({
                name: "",
                email: "",
                phone: "",
                subject: "",
                message: "",
                area: "",
            });
            try {
                e.currentTarget.reset();
            } catch {}
        } catch (err) {
            const detail = err?.response?.data?.detail;
            let msg = "Något gick fel. Vänligen försök igen eller ring oss direkt.";
            if (typeof detail === "string") msg = detail;
            else if (Array.isArray(detail) && detail[0]?.loc) {
                const fieldMap = {
                    name: "Namn",
                    email: "E-post",
                    subject: "Ämne",
                    message: "Meddelande",
                    phone: "Telefon",
                };
                const failed = detail
                    .map((d) => fieldMap[d.loc?.[1]] || d.loc?.[1])
                    .filter(Boolean);
                msg = `Kontrollera dessa fält: ${failed.join(", ")}.`;
            }
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            data-testid="contact-section"
            id="kontakt"
            className="relative py-24 bg-[#0A0A0B] border-t border-white/5"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7 }}
                        className="lg:col-span-5"
                    >
                        <p className="overline">Ta kontakt</p>
                        <h2 className="mt-4 font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                            Låt oss{" "}
                            <span className="italic text-[#C4A977]">
                                mötas
                            </span>
                            .
                        </h2>
                        <p className="mt-6 text-[#A1A1AA] text-base lg:text-lg leading-relaxed">
                            Fyll i formuläret så återkommer en av våra jurister
                            inom 24 timmar. Alla förfrågningar behandlas med
                            fullständig sekretess.
                        </p>

                        <div className="mt-10 space-y-5 text-sm">
                            <div className="flex items-start gap-4">
                                <span className="w-10 h-10 grid place-items-center border border-[#C4A977]/30 text-[#C4A977] shrink-0">
                                    <MapPin size={16} />
                                </span>
                                <div>
                                    <p className="overline !text-[0.65rem]">
                                        Besöksadress
                                    </p>
                                    <p className="mt-1 text-[#F4F4F5]">
                                        {FIRM.address}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <span className="w-10 h-10 grid place-items-center border border-[#C4A977]/30 text-[#C4A977] shrink-0">
                                    <Phone size={16} />
                                </span>
                                <div>
                                    <p className="overline !text-[0.65rem]">
                                        Telefon
                                    </p>
                                <a
                                    href={`tel:${FIRM.phoneTel}`}
                                    className="mt-1 block text-[#F4F4F5] hover:text-[#C4A977] transition-colors"
                                >
                                    {FIRM.phone}
                                </a>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <span className="w-10 h-10 grid place-items-center border border-[#C4A977]/30 text-[#C4A977] shrink-0">
                                    <Mail size={16} />
                                </span>
                                <div>
                                    <p className="overline !text-[0.65rem]">
                                        E-post
                                    </p>
                                    <a
                                        href={`mailto:${FIRM.email}`}
                                        className="mt-1 block text-[#F4F4F5] hover:text-[#C4A977] transition-colors"
                                    >
                                        {FIRM.email}
                                    </a>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <span className="w-10 h-10 grid place-items-center border border-[#C4A977]/30 text-[#C4A977] shrink-0">
                                    <Clock size={16} />
                                </span>
                                <div>
                                    <p className="overline !text-[0.65rem]">
                                        Öppettider
                                    </p>
                                    <p className="mt-1 text-[#F4F4F5]">
                                        {FIRM.hours}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <motion.form
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        onSubmit={onSubmit}
                        data-testid="contact-form"
                        className="lg:col-span-7 bg-[#111113] border border-white/5 p-8 lg:p-12 rounded-sm"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <Label
                                    htmlFor="name"
                                    className="text-[#A1A1AA] text-xs uppercase tracking-widest"
                                >
                                    Namn *
                                </Label>
                                <Input
                                    id="name"
                                    name="name"
                                    data-testid="contact-input-name"
                                    value={form.name}
                                    onChange={handle("name")}
                                    placeholder="För- och efternamn"
                                    className={`mt-2 ${inputBase}`}
                                    required
                                />
                            </div>
                            <div>
                                <Label
                                    htmlFor="email"
                                    className="text-[#A1A1AA] text-xs uppercase tracking-widest"
                                >
                                    E-post *
                                </Label>
                                <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    data-testid="contact-input-email"
                                    value={form.email}
                                    onChange={handle("email")}
                                    placeholder="namn@exempel.se"
                                    className={`mt-2 ${inputBase}`}
                                    required
                                />
                            </div>
                            <div>
                                <Label
                                    htmlFor="phone"
                                    className="text-[#A1A1AA] text-xs uppercase tracking-widest"
                                >
                                    Telefon
                                </Label>
                                <Input
                                    id="phone"
                                    name="phone"
                                    data-testid="contact-input-phone"
                                    value={form.phone}
                                    onChange={handle("phone")}
                                    placeholder="+46 70 000 00 00"
                                    className={`mt-2 ${inputBase}`}
                                />
                            </div>
                            <div>
                                <Label className="text-[#A1A1AA] text-xs uppercase tracking-widest">
                                    Rättsområde
                                </Label>
                                <Select
                                    value={form.area}
                                    onValueChange={(v) =>
                                        setForm((f) => ({ ...f, area: v }))
                                    }
                                >
                                    <SelectTrigger
                                        data-testid="contact-select-area"
                                        className={`mt-2 ${inputBase}`}
                                    >
                                        <SelectValue placeholder="Välj område" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#111113] border-white/10 text-[#F4F4F5]">
                                        {AREAS.map((a) => (
                                            <SelectItem
                                                key={a.id}
                                                value={a.title}
                                                className="focus:bg-[#C4A977]/15 focus:text-[#C4A977]"
                                            >
                                                {a.title}
                                            </SelectItem>
                                        ))}
                                        <SelectItem value="Annat">
                                            Annat
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="mt-5">
                            <Label
                                htmlFor="subject"
                                className="text-[#A1A1AA] text-xs uppercase tracking-widest"
                            >
                                Ämne *
                            </Label>
                            <Input
                                id="subject"
                                name="subject"
                                data-testid="contact-input-subject"
                                value={form.subject}
                                onChange={handle("subject")}
                                placeholder="Kort beskrivning"
                                className={`mt-2 ${inputBase}`}
                                required
                            />
                        </div>

                        <div className="mt-5">
                            <Label
                                htmlFor="message"
                                className="text-[#A1A1AA] text-xs uppercase tracking-widest"
                            >
                                Ditt meddelande *
                            </Label>
                            <Textarea
                                id="message"
                                name="message"
                                data-testid="contact-input-message"
                                value={form.message}
                                onChange={handle("message")}
                                placeholder="Beskriv ditt ärende så utförligt som möjligt."
                                rows={6}
                                className={`mt-2 ${inputBase} !h-auto py-3`}
                                required
                            />
                        </div>

                        <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <p className="text-xs text-[#A1A1AA] max-w-md">
                                Genom att skicka godkänner du att vi behandlar
                                dina personuppgifter för att återkomma i ditt
                                ärende.
                            </p>
                            <Button
                                type="submit"
                                disabled={loading}
                                data-testid="contact-submit-button"
                                className="bg-[#C4A977] hover:bg-[#DBC08F] text-black rounded-sm h-12 px-8 font-medium text-sm tracking-wide disabled:opacity-70"
                            >
                                {loading ? (
                                    <>
                                        <Loader2
                                            size={16}
                                            className="mr-2 animate-spin"
                                        />
                                        Skickar...
                                    </>
                                ) : (
                                    "Skicka förfrågan"
                                )}
                            </Button>
                        </div>
                    </motion.form>
                </div>
            </div>
        </section>
    );
}
